import { ChangeEvent, useEffect, useState, useCallback } from 'react';
import { minABI } from '@config/TiFi_min_abi';
import LockABI from '@config/abi/TiFiLock.json';
import { toast } from 'react-toastify';
import { T } from 'react-translator-component';
import StakeWidget from '../../Stake/components/StakeWidget';
import Button from '@components/elements/Button';
import { useWeb3React } from '@web3-react/core';
import ConnectWallet from '@components/widget/connect-wallet';
import { CONTRACT_ADDRESS, TIFI_ADDRESS, TOKENS, BSC_SCAN_URL, RPC_URL, APPROVAL_AMOUNT, ONE_DAY } from '@config/constants';
import { useNavigate } from 'react-router-dom';
import { BigNumber, ethers } from 'ethers';
import SwapCard from '@features/Swap/components/SwapCard';
import { SelectedProps } from '@components/elements/DropdownModal';
import { toFixed, getErrorMessage, extendToBigNumber } from '@utils/tifi';
import { useAlertActions } from '@hooks/useAlert';
import TermTable, { RowsProp } from '../components/TermTable';
import ConfirmModal from '../components/ConfirmModal';
import tifiRoutes from '@components/router/tifiRoutes';

const LockAdd = () => {
  const navigate = useNavigate();
  const [selectedCurrency, setSelectedCurrency] = useState<SelectedProps>(
    TOKENS[3],
  );
  const [openConnectModal, setOpenConnectModal] = useState<boolean>(false);
  const { account: address, library: provider } = useWeb3React();
  const [amount, setAmount] = useState<string>('0');
  const [balance, setBalance] = useState<number>(0);
  const [allowAmount, setAllowAmount] = useState<number>(0);
  const [availableBalance, setAvailableBalance] = useState(false);
  const [status, setStatus] = useState(false);
  const { setAlert } = useAlertActions();
  const [terms, setTerms] = useState<RowsProp[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [minAmount, setMinAmount] = useState(100000000000);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    let tmpval: any = e.target.value ? e.target.value : 0;
    if (tmpval < 0 || isNaN(tmpval)) {
      tmpval = amount;
    } else if (
      !(
        typeof tmpval === 'string' &&
        (tmpval.endsWith('.') || tmpval.startsWith('.'))
      )
    ) {
      tmpval = Number(e.target.value.toString());
    }
    setAvailableBalance(Number(tmpval) <= balance && Number(tmpval) >= minAmount);
    setAmount(tmpval);
  };

  const handleSelect = (_amount: number) => {
    setAmount(_amount.toString());
    setAvailableBalance(_amount <= balance && Number(_amount) >= minAmount);
  }

  const handleMax = async () => {
    setAvailableBalance(balance >= minAmount);
    setAmount(balance.toString());
  }

  const handleAddToken = async (token: SelectedProps) => {
    if (!provider) {
      setAlert({
        message: 'Please connect to wallet first!',
        type: 'notice',
        url: {
          link: ``,
          text: '',
        },
      });
      return
    }
    try {
      let decimal;
      let contract;
      const signer = provider.getSigner();
      contract = new ethers.Contract(token.address, minABI, signer);
      decimal = await contract.decimals();
      const wasAdded = await window.ethereum.request({
        method: "wallet_watchAsset",
        params: {
          type: "ERC20", // Initially only supports ERC20, but eventually more!
          options: {
            address: token.address,
            symbol: token.title, // A ticker symbol or shorthand, up to 5 chars.
            decimals: decimal,
          },
        },
      });
      if (wasAdded) {
        setAlert({
          message: `${token.title} ${T("successful added")}`,
          type: 'success',
          url: {
            link: '',
            text: '',
          },
        });
      } else {
        setAlert({
          message: `${token.title} ${T("added failed")}`,
          type: 'error',
          url: {
            link: '',
            text: '',
          },
        });
      }
    } catch (error) {
      setAlert({
        message: `${T("Cannot add address of")}, ${T(getErrorMessage(error))}`,
        type: 'error',
        url: {
          link: '',
          text: '',
        },
      });
    }
  };

  const getBalance = useCallback(async () => {
    try {
      const signer = provider.getSigner();
      let contract = new ethers.Contract(TIFI_ADDRESS, minABI, signer);
      let balance = await contract.balanceOf(address);
      balance = ethers.utils.formatUnits(balance);
      setAvailableBalance(Number(amount) <= Number(balance) && Number(amount) >= minAmount);
      setBalance(balance);
      return balance;
    } catch (error) {
      console.error('Cannot get TIFI balance', error);
      return 0;
    }
  }, [address, provider, amount, minAmount]);

  const getTerms = useCallback(async () => {
    try {
      const contract = new ethers.Contract(CONTRACT_ADDRESS.LOCK_ADDRESS, LockABI.abi, provider ? provider.getSigner()
        : new ethers.providers.JsonRpcProvider(RPC_URL));
      const response = await contract.getTerms(APPROVAL_AMOUNT);
      const _terms = response.map((item: BigNumber[]) => {
        return {
          period: item[0].toNumber() / ONE_DAY,
          minAmount: Number(ethers.utils.formatUnits(item[1])),
          rate: Number(ethers.utils.formatUnits(item[2])),
        };
      });
      setTerms(_terms);
      setMinAmount(_terms.reduce((p: any, c: any) => p.rate < c.rate ? p : c).minAmount);
    } catch (error) {
      console.log('Cannot get lock terms', error);
    }
  }, [provider,]);

  const checkAllowance = useCallback(async () => {
    try {
      const contract = new ethers.Contract(TIFI_ADDRESS, minABI, provider.getSigner());
      const allow = await contract.allowance(address, CONTRACT_ADDRESS.LOCK_ADDRESS);
      setAllowAmount(Number(ethers.utils.formatUnits(allow)));
    } catch (error) {
      console.error('Cannot get TIFI allowance', error);
    }
  }, [address, provider]);

  const handleApprove = async () => {
    setStatus(true);
    try {
      const contract = new ethers.Contract(TIFI_ADDRESS, minABI, provider.getSigner());
      const tx = await contract.approve(CONTRACT_ADDRESS.LOCK_ADDRESS, APPROVAL_AMOUNT);
      await tx.wait();
      setAlert({
        message: T('Approved! Now you can start earning.'),
        type: 'success',
        url: {
          link: `${BSC_SCAN_URL}/tx/${tx.hash}`,
          text: 'Check Transaction on BSCScan',
        },
      });
      await checkAllowance();
    } catch (error: any) {
      const supplyError = error?.error ? error.error : getErrorMessage(error);
      setAlert({
        message: supplyError,
        type: 'error',
        url: { link: '', text: '', },
      });
    }
    setStatus(false);
  }

  const getBestTerm = () => {
    let ret = terms.reduce((p, c) => p.rate < c.rate ? p : c);
    terms.forEach(term => {
      if (Number(amount) >= term.minAmount && term.rate > ret.rate) {
        ret = term;
      }
    });
    return ret;
  }

  const handleLock = async (term: RowsProp) => {
    if (address == null) {
      console.error('Please connect to your wallet first!');
      return;
    }
    setStatus(true);
    try {
      const contract = new ethers.Contract(CONTRACT_ADDRESS.LOCK_ADDRESS, LockABI.abi, provider.getSigner());
      const tx = await contract.deposit(term.period * ONE_DAY, extendToBigNumber(term.minAmount), extendToBigNumber(amount));
      setAlert({
        message: T('Transaction submitted! Please wait a few seconds for completion.'),
        type: 'notice',
        url: {
          link: `${BSC_SCAN_URL}/tx/${tx.hash}`,
          text: 'Check Transaction on BSCScan',
        },
      });
      await tx.wait();
      setAlert({
        message: 'Deposit Success!',
        type: 'success',
        url: {
          link: `${BSC_SCAN_URL}/tx/${tx.hash}`,
          text: 'Check Transaction on BSCScan',
        },
      });
      setAmount('0');
      navigate(tifiRoutes.STAKE_LOCK);
    } catch (error) {
      toast.error(T(getErrorMessage(error as any)));
    }
    setStatus(false);
  }

  useEffect(() => {
    if (address && provider) {
      getTerms();
      getBalance();
      checkAllowance();
    }
  }, [address, provider, getBalance, checkAllowance, getTerms]);

  return <StakeWidget title={T('Lock TiFi Token and Earn!')} onClick={() => navigate(-1)}>
    <TermTable rows={terms.sort((a, b) => a.rate > b.rate ? -1 : 1)} onClickRow={handleSelect} />
    <div>
      {openConnectModal && <ConnectWallet modalClose={() => setOpenConnectModal(false)} />}
      <SwapCard
        selectedCurrency={selectedCurrency}
        setSelectedCurrency={setSelectedCurrency}
        fromText="Input"
        balance={toFixed(balance, 10)}
        handleInputChange={(e) => handleChange(e)}
        inputValue={amount}
        amount={0}
        disabled={true}
        handleMax={handleMax}
        handleAddToken={handleAddToken}
      />
    </div>
    {address ? <div>
      {(allowAmount <= 0 || (allowAmount < Number(amount) && availableBalance)) && <Button
        className="bg-primary text-white"
        label={T('Enable Earning')}
        onClick={handleApprove}
      />}
      <div className="my-4">
        <Button
          disabled={!availableBalance || status || allowAmount < Number(amount) || Number(amount) === 0}
          className="bg-primary text-white"
          label={availableBalance ? (
            status && allowAmount >= Number(amount) ? T('Loading ...') : T('Start Earning!')
          ) : T('Insufficient balance')}
          onClick={() => { setShowModal(true); }}
          loading={status}
        />
      </div>
    </div> : <Button
      className="bg-primary text-white"
      label={'Connect Wallet'}
      onClick={() => setOpenConnectModal(true)}
    />}
    {showModal && <ConfirmModal
      term={getBestTerm()}
      amount={amount}
      handleConfirm={(term) => { setShowModal(false); handleLock(term); }}
      handleClose={() => { setShowModal(false); }}
    />}
  </StakeWidget>
}

export default LockAdd;